import React, { useState, useRef } from 'react';
import { MediaFile, MediaType } from '../../types';
import { MediaService } from '../../services/mediaService';

interface MediaSelectorProps {
  onMediaSelect: (mediaFiles: MediaFile[]) => void;
  maxFiles?: number;
  disabled?: boolean;
  acceptVideo?: boolean;
  className?: string;
}

const MediaSelector: React.FC<MediaSelectorProps> = ({
  onMediaSelect,
  maxFiles = 4,
  disabled = false,
  acceptVideo = true,
  className = ''
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setIsProcessing(true);

    try {
      // Valider chaque fichier
      const validationPromises = files.map(file => MediaService.validateMediaFile(file));
      const validations = await Promise.all(validationPromises);

      // Vérifier s'il y a des erreurs
      const errors = validations
        .map((validation, index) => ({ ...validation, file: files[index] }))
        .filter(v => !v.isValid);

      if (errors.length > 0) {
        alert(`Erreur(s) de validation:\n${errors.map(e => `- ${e.file.name}: ${e.error}`).join('\n')}`);
        return;
      }

      // Vérifier les limites
      if (files.length > maxFiles) {
        alert(`Maximum ${maxFiles} fichier(s) autorisé(s)`);
        return;
      }

      // Créer les objets MediaFile
      const mediaFiles: MediaFile[] = [];
      for (const file of files) {
        const mediaFile = await MediaService.createMediaFile(file);
        mediaFiles.push(mediaFile);
      }

      // Vérifications des règles de mix
      const types = mediaFiles.map(mf => mf.type);
      const hasVideo = types.includes('video');
      const hasImageOrGif = types.some(type => type === 'image' || type === 'gif');

      if (hasVideo && hasImageOrGif) {
        alert('Vous ne pouvez pas mélanger vidéos et images dans un même post');
        MediaService.cleanupMediaFiles(mediaFiles);
        return;
      }

      if (types.filter(t => t === 'video').length > 1) {
        alert('Maximum 1 vidéo autorisée par post');
        MediaService.cleanupMediaFiles(mediaFiles);
        return;
      }

      onMediaSelect(mediaFiles);
    } catch (error) {
      console.error('Erreur lors de la sélection des médias:', error);
      alert('Erreur lors de la sélection des médias');
    } finally {
      setIsProcessing(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    if (disabled || isProcessing) return;
    fileInputRef.current?.click();
  };

  const acceptTypes = acceptVideo 
    ? "image/*,video/*"
    : "image/*";

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isProcessing}
        className={`p-2 rounded-lg transition-colors cosmic-hover-effect ${
          disabled || isProcessing
            ? 'text-gray-500 cursor-not-allowed'
            : 'text-cyan-300 hover:text-cyan-200 hover:bg-emerald-800/20'
        } ${className}`}
        title={acceptVideo ? "Ajouter des images ou vidéos" : "Ajouter des images"}
      >
        {isProcessing ? (
          <div className="w-5 h-5 border-2 border-cyan-300 border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {acceptVideo ? (
              // Icône pour média (image + vidéo)
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" 
              />
            ) : (
              // Icône pour image seulement
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
              />
            )}
          </svg>
        )}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        multiple={maxFiles > 1}
        accept={acceptTypes}
        onChange={handleFileSelect}
        className="hidden"
      />
    </>
  );
};

export default MediaSelector;
